import { ICareerHubModuleConfig, CareerHubModuleContainer } from '@careerhub/common';
import { FluentProvider, teamsLightTheme } from '@fluentui/react-components';
import * as React from 'react';
import { BrowserRouter, useHistory } from 'react-router-dom';
import { getTelemetryConfig } from './Telemetry.config';
import { CareerHubNavigationService } from './services/careerHubNavigationService';
import { MainContent } from './components/MainContent';
import { OcvInit } from './utilities/ocv/ocvUtils';
import { MsalAuthenticationTemplate } from '@azure/msal-react';
import { InteractionType, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import { FrameContexts } from '@microsoft/teams-js';
import { MsalAuthenticationResult } from '@azure/msal-react';
import { enforceTabsterDummyAccessibility } from './utilities/tabsterA11y';

const MsalErrorComponent: React.FC<MsalAuthenticationResult> = ({ error }) => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <h1>Authentication Error</h1>
      <p>{error?.message || 'An error occurred during sign-in. Please try again.'}</p>
      <button
        onClick={() => window.location.reload()}
        style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
      >
        Retry
      </button>
    </div>
  );
};

export const App: React.FC<{ instance: PublicClientApplication }> = (props: { instance: PublicClientApplication }) => {
  const history = useHistory();
  const ocvRef = React.createRef<HTMLButtonElement>();
  const { instance } = props;

  React.useEffect(() => {
    const initializeFeedback = async () => {
      OcvInit(ocvRef);
    };
    initializeFeedback();
  });

  React.useEffect(() => enforceTabsterDummyAccessibility(document), []);

  const careerHubModuleConfig: ICareerHubModuleConfig = {
    careerHubTokenProvider: async (resourceId: string): Promise<string> => {
      const appResourceId = resourceId || __CLIENT_ID__;
      const scope = appResourceId + '/.default';
      try {
        const authResult = await props.instance.acquireTokenSilent({
          scopes: [scope],
          account: instance.getActiveAccount() || instance.getAllAccounts()[0],
        });
        return authResult.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await props.instance.acquireTokenRedirect({ scopes: [scope] });
          return '';
        }
        throw error;
      }
    },
    instance: instance,
    appInsightsConfig: getTelemetryConfig(history),
    hostClientName: 'careerhub-web',
    apiResources: { ...__API__ },
    navigationService: new CareerHubNavigationService(),
    hrApimSubscriptionKey: __HR_APIM_SUBSCRIPTION_KEY__,
  };

  const frameContext = window.parent != window.self ? FrameContexts.task : FrameContexts.content;

  const authRequest = {
    scopes: ['openid', 'profile', 'User.Read'],
  };

  return (
    <FluentProvider theme={teamsLightTheme}>
      {!__IS_E2E_TESTING__ && (
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={authRequest}
          errorComponent={MsalErrorComponent}
        >
          <CareerHubModuleContainer config={careerHubModuleConfig}>
            <BrowserRouter>
              {/* App header */}
              <MainContent frameContext={frameContext} showHeader={true} />
            </BrowserRouter>
          </CareerHubModuleContainer>
        </MsalAuthenticationTemplate>
      )}
      <div id="inAppFeedbackRef-id"></div>
    </FluentProvider>
  );
};

export default App;
